import { FC, useEffect, useState } from 'react';
import Typography from '@components/common/typography';
import styles from './styles.module.scss';
import { useUserContext } from '@hooks/context/useUserContext';
import { IModalComponentProps } from '@context/modal_context';
import { formatDate, getGiftCardStatusVariant, prettifyAmount } from '@utils/index';
import { useForm } from '@hooks/helpers/useForm';
import { FormProvider } from 'react-hook-form';
import { TGiftCard, TGiftCardListItem } from '@xeppt/xeppt-sdk/types';
import { Icon } from '@components/icons';
import Status from '@components/common/status';
import { getGiftCardStatusesList } from '@utils/billing';
import { currencies } from '@const/currency';
import Button from '@components/common/button';
import { useLocales } from '@hooks/helpers/useLocales';

const ActivateGiftCardModal: FC<
    IModalComponentProps<{
        giftCards: (TGiftCard | TGiftCardListItem)[];
        refetchGiftCards?: () => void;
        isUploaded?: boolean;
    }>
> = ({ modalData, handleChangeDisable, onClose, onSubmit, isLoadingSubmit }) => {
    const { account } = useUserContext();
    const { submitLocale } = useLocales();
    const [isInsufficient, setIsInsufficient] = useState(false);
    const form = useForm({
        defaultValues: {
            amount: '',
            reference: ''
        }
    });
    const { giftCards, isUploaded } = modalData;

    useEffect(() => {
        const totalAmount = giftCards.reduce((acc, card) => acc + card.amount, 0);
        if (account && totalAmount > account.balance) {
            handleChangeDisable(true);
            setIsInsufficient(true);
        } else {
            handleChangeDisable(false);
            setIsInsufficient(false);
        }
    }, [account, giftCards]);

    // Single-card activation renders the same premium layout as the gift-card detail
    // drawer (hero card + detail rows), minus the redemption-history tab. Bulk / CSV
    // activation keeps the multi-card summary layout below.
    const isSingle = !isUploaded && giftCards.length === 1;
    const card = giftCards[0];
    const currency = (card?.currency as string) ?? 'CAD';
    const symbol = currencies[currency as 'CAD']?.symbol ?? '$';
    const totalReserve = giftCards.reduce((acc, item) => acc + item.amount, 0);

    const recipientLabel = card?.email
        ? card.email
        : card?.phone_code && card?.phone_number
          ? `${card.phone_code.includes('+') ? card.phone_code : '+' + card.phone_code} ${card.phone_number}`
          : '-';

    return (
        <FormProvider {...form}>
            <div className={styles.wrapper}>
                <div className={styles.modal_header}>
                    <Typography fz={24} fw={700} alignment="center">
                        Activate gift card
                    </Typography>
                    <button type="button" className={styles.close_btn} onClick={onClose}>
                        <Icon name="close" width={24} height={24} />
                    </button>
                </div>
                <div className={styles.balance_wrapper}>
                    <div className={styles.balance}>
                        <Typography variant="body3">
                            Current XEPPT wallet balance:{' '}
                            <b>
                                {symbol}
                                {prettifyAmount((account?.balance || 0).toFixed(2) || '')}
                            </b>
                        </Typography>
                    </div>
                    {isInsufficient && (
                        <div className={styles.insufficient_warning}>
                            <Icon name="filled_warning" />{' '}
                            <Typography variant="body4">Insufficient balance</Typography>
                        </div>
                    )}
                </div>
                {isSingle ? (
                    <div className={styles.detail_content}>
                        <div className={styles.hero_card}>
                            <div className={styles.hero_icon}>
                                <Icon name="gift_card" width={28} height={28} />
                            </div>
                            <p className={styles.hero_serial}>{card.gift_card_serial_number}</p>
                            <Status variant={getGiftCardStatusVariant(card.status)}>
                                {
                                    getGiftCardStatusesList.find(
                                        (item) => item.value === card.status
                                    )?.label
                                }
                            </Status>
                            <p className={styles.hero_balance_label}>Gift card balance</p>
                            <p className={styles.hero_amount}>
                                {symbol}
                                {prettifyAmount((card.available_amount ?? card.amount ?? 0).toFixed(2))}{' '}
                                {currency}
                            </p>
                        </div>
                        <div className={styles.details}>
                            <div className={styles.row}>
                                <p>Original amount</p>
                                <p>
                                    {symbol}
                                    {prettifyAmount((card.amount ?? 0).toFixed(2))} {currency}
                                </p>
                            </div>
                            <div className={styles.row}>
                                <p>Issued</p>
                                <p>{formatDate(card.created_at, 'DD MMMM YYYY')}</p>
                            </div>
                            <div className={styles.row}>
                                <p>Recipient</p>
                                <p>{recipientLabel}</p>
                            </div>
                            <div className={styles.row}>
                                <p>Amount that will be reserved</p>
                                <p className={styles.reserved_value}>
                                    {symbol}
                                    {prettifyAmount((card.amount ?? 0).toFixed(2))} {currency}
                                </p>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className={styles.summary}>
                        <div className={styles.block}>
                            <Typography fz={16} fw={600} className={styles.block_title}>
                                Gift cards details
                            </Typography>
                            {giftCards.length > 1 ? (
                                <div className={styles.content}>
                                    <div className={`${styles.row} ${styles.map}`}>
                                        <Typography variant="body4" className={styles.row_label}>
                                            Gift card numbers and values
                                        </Typography>
                                        <div>
                                            {giftCards.map((item) => (
                                                <Typography variant="body4" key={item.id}>
                                                    {item.gift_card_serial_number} - $
                                                    {prettifyAmount(item.amount.toFixed(2))}
                                                    {item.currency}
                                                </Typography>
                                            ))}
                                        </div>
                                    </div>
                                    <div className={styles.row}>
                                        <Typography variant="body4" className={styles.row_label}>
                                            Quantity of gift cards
                                        </Typography>
                                        <span>{giftCards.length}</span>
                                    </div>
                                    <div className={styles.row}>
                                        <Typography variant="body4" className={styles.row_label}>
                                            Amount that will be reserved
                                        </Typography>
                                        <span className={styles.row_value_bold}>
                                            ${totalReserve.toFixed(2)}CAD
                                        </span>
                                    </div>
                                </div>
                            ) : (
                                <div className={styles.content}>
                                    <div className={styles.row}>
                                        <Typography variant="body4" className={styles.row_label}>
                                            Gift card number
                                        </Typography>
                                        <div>
                                            {giftCards.map((item) => (
                                                <Typography variant="body4" key={item.id}>
                                                    {item.gift_card_serial_number}
                                                </Typography>
                                            ))}
                                        </div>
                                    </div>
                                    <div className={styles.row}>
                                        <Typography variant="body4" className={styles.row_label}>
                                            Gift card value
                                        </Typography>
                                        <span>${totalReserve.toFixed(2)} CAD</span>
                                    </div>
                                    <div className={styles.row}>
                                        <Typography variant="body4" className={styles.row_label}>
                                            Amount that will be reserved
                                        </Typography>
                                        <span className={styles.row_value_bold}>
                                            ${totalReserve.toFixed(2)} CAD
                                        </span>
                                    </div>
                                </div>
                            )}
                        </div>
                        {(isUploaded || giftCards.length === 1) && (
                            <div className={styles.divider} />
                        )}
                        {(isUploaded || giftCards.length === 1) && (
                            <div className={styles.block}>
                                <Typography fz={16} fw={600} className={styles.block_title}>
                                    Recipients details
                                </Typography>
                                <div className={styles.row}>
                                    <Typography variant="body4" className={styles.row_label}>
                                        Recipient email
                                    </Typography>
                                    <span>
                                        {isUploaded
                                            ? 'From uploaded CSV'
                                            : giftCards?.length > 1
                                              ? 'Bulk activation'
                                              : recipientLabel}
                                    </span>
                                </div>
                            </div>
                        )}
                    </div>
                )}
                {/* Own sticky footer — matches the gift-card details drawer action bar.
                    Activate calls the shared onSubmit (the activate action wired in
                    useGiftCardsModals); loading + insufficient-balance disable are honoured. */}
                <div className={styles.actions_wrapper}>
                    <div className={styles.actions}>
                        <Button variant="outlined-primary" size="medium" onClick={onClose}>
                            {submitLocale('cancel')}
                        </Button>
                        <Button
                            variant="primary"
                            size="medium"
                            disabled={isInsufficient}
                            isLoading={isLoadingSubmit}
                            onClick={() => onSubmit?.(modalData)}>
                            {submitLocale('activate')}
                        </Button>
                    </div>
                </div>
            </div>
        </FormProvider>
    );
};

export default ActivateGiftCardModal;
